import { useEffect, useMemo } from 'react';
import { LocalizedLink as Link } from '../components/LocalizedLink';
import { useTranslation, Trans } from 'react-i18next';
import { Check, X, CreditCard, ShieldCheck, Sparkles } from 'lucide-react';
import { SEO } from '../components/SEO';
import { useLocalizedSeo } from '../hooks/useLocalizedSeo';
import { PublicHeader } from '../components/PublicHeader';
import { PublicFooter } from '../components/PublicFooter';
import { usePublicFreeLimits } from '../hooks/usePublicFreeLimits';

const SITE_URL = 'https://bolaoelite.com.br';
const PREMIUM_PRICE_BRL = 9.90;

export default function Precos() {
  const { t } = useTranslation('institutional');
  const { limits } = usePublicFreeLimits();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const freePoolNoun =
    limits.maxPrivatePoolsCreated === 1
      ? t('pricing.free_pool_singular', 'bolão privado criado')
      : t('pricing.free_pool_plural', 'bolões privados criados');

  const jsonLd = useMemo(
    () => [
      {
        '@context': 'https://schema.org',
        '@type': 'Product',
        '@id': `${SITE_URL}/precos#product`,
        name: 'Bolão Elite — Planos e Preços',
        description:
          'Planos do Bolão Elite: Free gratuito para sempre, Premium por R$ 9,90/mês. Tabela comparativa, formas de pagamento e política de cancelamento.',
        url: `${SITE_URL}/precos`,
        brand: { '@type': 'Brand', name: 'Bolão Elite' },
        offers: [
          {
            '@type': 'Offer',
            name: 'Plano Free',
            url: `${SITE_URL}/precos`,
            priceCurrency: 'BRL',
            price: '0.00',
            availability: 'https://schema.org/InStock',
            eligibleRegion: { '@type': 'Country', name: 'BR' },
          },
          {
            '@type': 'Offer',
            name: 'Plano Premium',
            url: `${SITE_URL}/premium`,
            priceCurrency: 'BRL',
            price: PREMIUM_PRICE_BRL.toFixed(2),
            priceSpecification: {
              '@type': 'UnitPriceSpecification',
              price: PREMIUM_PRICE_BRL.toFixed(2),
              priceCurrency: 'BRL',
              unitCode: 'MON',
              unitText: 'mês',
            },
            availability: 'https://schema.org/InStock',
            eligibleRegion: { '@type': 'Country', name: 'BR' },
            seller: { '@type': 'Organization', name: 'Bolão Elite' },
          },
        ],
      },
    ],
    []
  );

  const seo = useLocalizedSeo('precos');

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <SEO
        title={t('pricing.seo_title', 'Preços do Bolão Elite — Free de graça e Premium por R$ 9,90/mês')}
        description={t('pricing.seo_desc', 'Veja os planos do Bolão Elite. O plano gratuito tem tudo o que você precisa pra jogar bolão; o Premium custa R$ 9,90/mês com palpite cego e sem anúncios.')}
        canonical={seo.canonical}
        ogUrl={seo.ogUrl}
        locale={seo.locale}
        alternates={seo.alternates}
        keywords="preços bolão elite, planos, free premium, assinatura mensal, R$ 9,90"
        jsonLd={jsonLd}
      />

      <PublicHeader />

      <section className="bg-gradient-to-br from-primary-700 via-primary-800 to-primary-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <nav className="text-sm text-white/70 mb-3" aria-label="breadcrumb">
            <Link to="/" className="hover:text-white transition-colors">{t('common:nav.home', 'Início')}</Link>
            <span className="mx-2">/</span>
            <span className="text-white">{t('pricing.breadcrumb', 'Preços')}</span>
          </nav>
          <div className="text-center">
            <span className="inline-block text-xs font-semibold text-primary-200 uppercase tracking-widest">
              {t('pricing.eyebrow', 'Planos')}
            </span>
            <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold tracking-tight leading-tight">
              {t('pricing.h1', 'Preços simples, sem letra miúda')}
            </h1>
            <p className="mt-5 text-base sm:text-lg text-white/80 leading-relaxed max-w-2xl mx-auto">
              {t('pricing.intro', 'Um plano gratuito completo e um plano Premium opcional. Você escolhe quando (e se) faz upgrade. Sem fidelidade, sem multa, sem trial enrolado.')}
            </p>
          </div>
        </div>
      </section>

      <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 py-12 sm:py-16 space-y-16">
        {/* Cards de planos */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl mx-auto">
          {/* Card Free */}
          <article className="bg-white border-2 border-gray-200 rounded-2xl p-6 sm:p-8 flex flex-col">
            <header className="mb-6">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-widest">
                {t('pricing.free_eyebrow', 'Para começar')}
              </span>
              <h2 className="mt-2 text-2xl font-extrabold text-gray-900">{t('pricing.free_name', 'Free')}</h2>
              <p className="mt-1 text-sm text-gray-500">{t('pricing.free_tagline', 'Grátis para sempre')}</p>
            </header>
            <div className="mb-6">
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-black text-gray-900 tabular-nums">{t('pricing.free_price', 'R$ 0')}</span>
                <span className="text-sm text-gray-500 ml-1">{t('pricing.per_month', '/mês')}</span>
              </div>
            </div>
            <ul className="space-y-3 mb-6 flex-1">
              <PlanFeature included>
                {t('pricing.free_f1', {
                  defaultValue: '{{count}} {{noun}} (até {{members}} participantes)',
                  count: limits.maxPrivatePoolsCreated,
                  noun: freePoolNoun,
                  members: limits.maxPrivatePoolMembers,
                })}
              </PlanFeature>
              <PlanFeature included>
                {t('pricing.free_f2', {
                  defaultValue: 'Entrar em até {{joined}} bolões privados como convidado',
                  joined: limits.maxPrivatePoolsJoined,
                })}
              </PlanFeature>
              <PlanFeature included>{t('pricing.free_f3', 'Participar de bolões públicos sem limite')}</PlanFeature>
              <PlanFeature included>{t('pricing.free_f4', 'Palpitar em todas as competições')}</PlanFeature>
              <PlanFeature included>{t('pricing.free_f5', 'Ranking em tempo real')}</PlanFeature>
              <PlanFeature included>{t('pricing.free_f6', 'Conquistas e notificações')}</PlanFeature>
              <PlanFeature>{t('pricing.free_f7', 'Navegação sem anúncios')}</PlanFeature>
              <PlanFeature>{t('pricing.free_f8', 'Prioridade em novas funcionalidades')}</PlanFeature>
            </ul>
            <Link
              to="/"
              className="block w-full text-center px-6 py-3 text-primary-700 font-bold border border-primary-300 rounded-lg hover:bg-primary-50 transition-colors"
            >
              {t('pricing.free_cta', 'Criar conta grátis')}
            </Link>
          </article>

          {/* Card Premium */}
          <article className="bg-gradient-to-br from-amber-50 to-amber-100 border-2 border-amber-400 rounded-2xl p-6 sm:p-8 flex flex-col relative">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-white text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full">
              {t('pricing.prem_badge', 'Mais completo')}
            </div>
            <header className="mb-6">
              <span className="text-xs font-semibold text-amber-700 uppercase tracking-widest">
                {t('pricing.prem_eyebrow', 'Para quem quer mais')}
              </span>
              <h2 className="mt-2 text-2xl font-extrabold text-gray-900 flex items-center gap-2">
                {t('pricing.prem_name', 'Premium')}
                <Sparkles className="w-5 h-5 text-amber-500" />
              </h2>
              <p className="mt-1 text-sm text-amber-700">{t('pricing.prem_tagline', 'Cancele quando quiser')}</p>
            </header>
            <div className="mb-6">
              <div className="flex items-baseline gap-1">
                <span className="text-sm text-gray-500">R$</span>
                <span className="text-4xl font-black text-gray-900 tabular-nums">{t('pricing.prem_price_amount', '9,90')}</span>
                <span className="text-sm text-gray-500 ml-1">{t('pricing.per_month', '/mês')}</span>
              </div>
              <p className="mt-2 text-xs text-gray-500">{t('pricing.prem_price_note', 'Cobrança mensal recorrente · Cartão de crédito')}</p>
            </div>
            <ul className="space-y-3 mb-6 flex-1">
              <PlanFeature included>{t('pricing.prem_f1', 'Bolões privados ilimitados')}</PlanFeature>
              <PlanFeature included>{t('pricing.prem_f2', 'Participantes ilimitados em cada bolão')}</PlanFeature>
              <PlanFeature included>{t('pricing.prem_f3', 'Quem você convida não precisa ser Premium')}</PlanFeature>
              <PlanFeature included>{t('pricing.prem_f4', 'Entrar em quantos bolões quiser')}</PlanFeature>
              <PlanFeature included>{t('pricing.prem_f5', 'Navegação sem anúncios')}</PlanFeature>
              <PlanFeature included>{t('pricing.prem_f6', 'Prioridade em novas funcionalidades')}</PlanFeature>
            </ul>
            <Link
              to="/assinatura"
              className="block w-full text-center px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-lg transition-colors shadow"
            >
              {t('pricing.prem_cta', 'Assinar Premium')}
            </Link>
            <Link to="/premium" className="block mt-3 text-center text-xs text-amber-700 hover:underline font-semibold">
              {t('pricing.prem_details_link', 'Ver detalhes do Premium →')}
            </Link>
          </article>
        </section>

        {/* Forma de pagamento + cancelamento */}
        <section className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div className="bg-white border border-gray-200 rounded-2xl p-6">
            <div className="w-11 h-11 bg-primary-100 text-primary-700 rounded-xl flex items-center justify-center mb-4">
              <CreditCard className="w-5 h-5" />
            </div>
            <h3 className="text-base font-bold text-gray-900 mb-2">{t('pricing.pay_title', 'Formas de pagamento')}</h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              <Trans i18nKey="pricing.pay_text" t={t}>
                Atualmente aceitamos <strong>cartão de crédito</strong> (Visa, Mastercard, Elo, Amex). Pagamento processado por gateway PCI-DSS; não armazenamos dados completos do cartão. Outras formas (Pix, débito) em breve.
              </Trans>
            </p>
          </div>
          <div className="bg-white border border-gray-200 rounded-2xl p-6">
            <div className="w-11 h-11 bg-emerald-100 text-emerald-700 rounded-xl flex items-center justify-center mb-4">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <h3 className="text-base font-bold text-gray-900 mb-2">{t('pricing.cancel_title', 'Cancelamento')}</h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              <Trans i18nKey="pricing.cancel_text" t={t}>
                Cancele a qualquer momento em <em>Minha conta → Assinatura</em>, sem multa e sem fidelidade. Você mantém os benefícios Premium até o fim do ciclo já pago e depois a conta volta ao plano gratuito.
              </Trans>
            </p>
          </div>
        </section>

        {/* Comparativo completo */}
        <section>
          <div className="text-center mb-6">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
              {t('pricing.compare_title', 'Comparativo detalhado')}
            </h2>
          </div>
          <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-4 sm:px-6 py-4 font-semibold text-gray-700">{t('pricing.col_feature', 'Funcionalidade')}</th>
                  <th className="text-center px-3 sm:px-6 py-4 font-semibold text-gray-700">{t('pricing.col_free', 'Free')}</th>
                  <th className="text-center px-3 sm:px-6 py-4 font-semibold text-amber-700">{t('pricing.col_premium', 'Premium')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <CompareRow feature={t('pricing.cmp1_feature', 'Bolões privados que você cria')} free={String(limits.maxPrivatePoolsCreated)} premium={t('pricing.unlimited', 'ilimitados')} />
                <CompareRow feature={t('pricing.cmp2_feature', 'Participantes por bolão privado seu')} free={t('pricing.up_to', 'até {{n}}', { n: limits.maxPrivatePoolMembers })} premium={t('pricing.unlimited', 'ilimitados')} />
                <CompareRow feature={t('pricing.cmp3_feature', 'Bolões privados como convidado')} free={t('pricing.up_to', 'até {{n}}', { n: limits.maxPrivatePoolsJoined })} premium={t('pricing.unlimited', 'ilimitados')} />
                <CompareRow feature={t('pricing.cmp4_feature', 'Bolões públicos')} free={t('pricing.unlimited', 'ilimitados')} premium={t('pricing.unlimited', 'ilimitados')} />
                <CompareRow feature={t('pricing.cmp5_feature', 'Palpitar em todas as competições')} free={true} premium={true} />
                <CompareRow feature={t('pricing.cmp6_feature', 'Ranking do bolão em tempo real')} free={true} premium={true} />
                <CompareRow feature={t('pricing.cmp7_feature', 'Ranking global da plataforma')} free={true} premium={true} />
                <CompareRow feature={t('pricing.cmp8_feature', 'Conquistas e medalhas')} free={true} premium={true} />
                <CompareRow feature={t('pricing.cmp9_feature', 'Notificações de lembrete')} free={true} premium={true} />
                <CompareRow feature={t('pricing.cmp10_feature', 'Navegação sem anúncios')} free={false} premium={true} />
                <CompareRow feature={t('pricing.cmp11_feature', 'Prioridade em novas funcionalidades')} free={false} premium={true} />
              </tbody>
            </table>
          </div>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}

function PlanFeature({ included = false, children }: { included?: boolean; children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2 text-sm">
      {included ? (
        <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
      ) : (
        <X className="w-5 h-5 text-gray-300 flex-shrink-0 mt-0.5" aria-hidden="true" />
      )}
      <span className={included ? 'text-gray-700' : 'text-gray-400'}>{children}</span>
    </li>
  );
}

type CompareValue = boolean | string;

function CompareCell({ value, color }: { value: CompareValue; color: 'emerald' | 'amber' }) {
  const { t } = useTranslation('institutional');
  if (typeof value === 'string') {
    const colorClass = color === 'emerald' ? 'text-emerald-700' : 'text-amber-700';
    return <span className={`text-xs font-bold ${colorClass}`}>{value}</span>;
  }
  if (value) {
    const colorClass = color === 'emerald' ? 'text-emerald-600' : 'text-amber-600';
    return <Check className={`w-5 h-5 ${colorClass} inline`} aria-label={t('pricing.included', 'Incluído')} />;
  }
  return <X className="w-5 h-5 text-gray-300 inline" aria-label={t('pricing.not_included', 'Não incluído')} />;
}

function CompareRow({ feature, free, premium }: { feature: string; free: CompareValue; premium: CompareValue }) {
  return (
    <tr>
      <td className="px-4 sm:px-6 py-3 text-gray-700">{feature}</td>
      <td className="px-3 sm:px-6 py-3 text-center"><CompareCell value={free} color="emerald" /></td>
      <td className="px-3 sm:px-6 py-3 text-center"><CompareCell value={premium} color="amber" /></td>
    </tr>
  );
}
